// Tipos de pregunta StoreHub (catálogo question_type)
export const QUESTION_TYPES = {
  1: { id: 1, name: 'Text', label: 'Párrafo', icon: 'HiOutlineMenuAlt2', hasOptions: false, hasPoints: true, isStructural: false },
  2: { id: 2, name: 'Selector', label: 'Selector', icon: 'HiOutlineSelector', hasOptions: true, hasPoints: true, isStructural: false },
  3: { id: 3, name: 'Checkbox', label: 'Selección múltiple', icon: 'HiOutlineCheckCircle', hasOptions: true, hasPoints: true, isStructural: false },
  4: { id: 4, name: 'Radio', label: 'Selección única', icon: 'HiOutlineDotsCircleHorizontal', hasOptions: true, hasPoints: true, isStructural: false },
  5: { id: 5, name: 'TextArea', label: 'Texto largo', icon: 'HiOutlineDocumentText', hasOptions: false, hasPoints: true, isStructural: false },
  6: { id: 6, name: 'Date', label: 'Fecha', icon: 'HiOutlineCalendar', hasOptions: false, hasPoints: true, isStructural: false },
  7: { id: 7, name: 'Menu', label: 'Menú', icon: 'HiOutlineViewList', hasOptions: true, hasPoints: true, isStructural: false },
  8: { id: 8, name: 'Title', label: 'Sección', icon: 'HiOutlineHashtag', hasOptions: false, hasPoints: false, isStructural: true },
  9: { id: 9, name: 'FilePicker', label: 'Foto / archivo', icon: 'HiOutlinePhotograph', hasOptions: false, hasPoints: true, isStructural: false },
  // La misma foto con validación visual con IA. No se elige en el desplegable: sale sola al
  // elegir la validación (`utils/validacionVisual.js`). Sus opciones `Sí`/`No` las elige la
  // IA, así que no se muestran ni se editan.
  10: { id: 10, name: 'AIVisualCheck', label: 'Foto / archivo', icon: 'HiOutlinePhotograph', hasOptions: false, hasPoints: true, isStructural: false },
}

export const RECOMMENDED_TYPES_ORDER = [4, 9, 8, 5, 3, 6]

// El builder ofrece un solo tipo de texto libre ("Párrafo", 5) y una sola forma de
// elegir una opción entre varias ("Selección única", 4). Los ids 1, 2 y 7 siguen
// existiendo en el catálogo de StoreHub, pero no aportan nada distinto: al responder el
// ticket, `QuestionTypeMenu.js` es copia literal de `QuestionTypeSelector.js`, y ese
// selector es la misma pregunta que el radio, mostrada desplegada. Lo que llega con
// esos ids se convierte al entrar, así no queda una pregunta apuntando a un tipo que el
// desplegable ya no lista.
const TIPOS_UNIFICADOS = { 1: 5, 2: 4, 7: 4 }
export const normalizeQuestionType = (id) => TIPOS_UNIFICADOS[id] ?? id

// Lo que muestra el desplegable de tipo: la foto con validación (10) es "Foto / archivo".
export const tipoDelDesplegable = (id) => (id === 10 ? 9 : id)

// La incidencia y la tarea automática de la pregunta ENTERA solo existen en los tipos
// sin opciones: donde hay opciones se marcan en cada una, y el panel no dibuja los
// controles de la pregunta. Pasar a un tipo con opciones las deja activas e invisibles,
// y viajan igual al backend porque el payload del grupo no filtra por tipo.
//
// Vive acá, al lado de `normalizeQuestionType`, porque la regla se aplica desde lados
// que no se conocen entre sí: el panel de una pregunta y el de varias. Con la regla
// copiada, sumar un campo nuevo que dependa de `hasOptions` obligaba a acordarse de
// cada copia. `fusionarRespuesta.js` la repite a propósito: es un módulo puro que
// recibe `hasOptions` por parámetro para poder probarse sin el resto del builder.
export const ADORNOS_SIN_OPCIONES = { incidents: false, generate_ticket: false, payload: null }

// Los tres campos sin los cuales el backend rechaza la asignación de la tarea. Mientras
// falte alguno no hay tarea que crear, así que `generate_ticket` se deriva de esto y no
// del interruptor: marcarlo abre los campos, y lo que enciende la tarea de verdad es
// terminar de completarlos. Sin esta regla, abrir el bloque y salir sin elegir nada
// dejaba la opción marcada como "genera tarea" con un payload vacío: se veía encendida
// en el panel y no creaba nada.
export const tareaConfigurada = (payload) =>
  !!(payload && payload.parent_category && payload.ticket_category_id && payload.operator)

// Los mismos tres campos, en el orden en que se llenan, para poder decir cuál falta. La
// descripción queda afuera: es opcional y no participa de `tareaConfigurada`.
//
// `falta` es lo que se lee debajo del campo vacío cuando se intenta cerrar la tarea sin
// terminarla. Dice qué hacer y no qué pasó: al lado del control que hay que tocar, "elegí
// una categoría" resuelve el problema y "falta la categoría" solo lo nombra.
export const CAMPOS_DE_TAREA = [
  { clave: 'parent_category', etiqueta: 'Categoría', falta: 'Elegí una categoría' },
  { clave: 'ticket_category_id', etiqueta: 'Tarea', falta: 'Elegí la tarea que se crea' },
  { clave: 'operator', etiqueta: 'Se asigna a', falta: 'Elegí a quién se le asigna' },
]

/** Las claves que faltan para que la tarea exista. Vacío si está lista. */
export const faltantesDeTarea = (payload) =>
  CAMPOS_DE_TAREA.filter(({ clave }) => !payload?.[clave]).map(({ clave }) => clave)

/**
 * Nadie tocó nada acá. Se distingue de "a medio llenar" porque son dos situaciones
 * distintas: cerrar una tarea en blanco es no haberla querido, y se descarta sin decir
 * nada; cerrar una empezada es perder trabajo, y eso sí hay que avisarlo.
 */
export const tareaEnBlanco = (payload) =>
  faltantesDeTarea(payload).length === CAMPOS_DE_TAREA.length && !payload?.description

// Cuántas opciones entran a la vista antes de que la lista abierta se coma la hoja. De
// ahí para arriba la pregunta se muestra como desplegable mientras no se la edita. Es
// una decisión de presentación: el tipo de la pregunta no cambia.
export const MAX_OPCIONES_A_LA_VISTA = 4

// Lo que ocupa la barra del asistente contra el piso de la pantalla: su velo desenfocado
// (`AIChat`) más la barra en sí. Flota encima de todo lo que scrollea, así que cualquier
// columna con scroll propio tiene que reservar este aire abajo o su última fila queda
// tapada al llegar al final.
//
// Vive acá y no en `AIChat` porque lo consume también el mapa del índice (`OutlineMap`),
// que está del otro lado de la hoja y no tiene forma de enterarse de que cambió.
export const ALTO_BARRA_ASISTENTE = '11rem'

// Endpoint n8n para generación AI (workflow [StoreHub] Builder AI)
export const AI_WEBHOOK_URL = 'https://automation.cubiq.digital/webhook/builder-ai-generate'

// Solo para mostrar en el header del chat. El modelo real lo define el workflow
// n8n (nodo OpenAI Chat Completions), no el front: si cambia allá, actualizar acá.
export const AI_MODEL_LABEL = 'gpt-5.6-luna'

export const BUILDER_MODES = {
  GENERATE: 'generate',
  EDIT: 'edit',
  ADD_QUESTIONS: 'add_questions',
}

// Roles del cuestionario dentro de la categoría (modelo confirmado por Jesús).
//
// El rol NO es un campo del cuestionario: vive en la categoría de tarea, que apunta a uno
// como `question_group_primary_id` y a otro como `question_group_secondary_id`
// (`crud/formIssuesTypes/AddOrEditIssuesType.js`). El mismo cuestionario puede ser de
// información en una categoría y de cierre en otra, así que acá el rol es con cuál de las
// dos vistas se lo está mirando, no dónde va a quedar guardado.
// Los rótulos dicen CUÁNDO se responde, no cómo se llama el campo. "Información" y
// "Cierre" son los nombres del modelo (`question_group_primary_id` /
// `question_group_secondary_id`) y no significan nada para quien arma el cuestionario:
// eligiendo entre esas dos palabras no hay forma de saber que una pone las preguntas
// adentro del alta de la tarea y la otra las pone sobre una tarea que ya existe.
export const QUESTIONNAIRE_ROLES = {
  INFO: {
    value: 'primary',
    label: 'Al crear la tarea',
    description: 'Las preguntas van dentro del formulario de alta, junto con los datos de la tarea.',
  },
  CLOSE: {
    value: 'secondary',
    label: 'Para cerrar la tarea',
    description: 'Se responde sobre una tarea ya abierta, y es lo que permite cerrarla.',
  },
}

// Qué le pasa a la tarea cuando alguien termina de responder el cuestionario. A
// diferencia del rol, esto SÍ es un campo del cuestionario (`question_group.action`), y
// lo ejecuta `handleQuestionnaireResolution` en
// `tickets-mytasks/components/TicketMyTaskContent.js` al mandar las respuestas.
//
// `null` no es "sin configurar": es la opción que deja la tarea como está, y es lo que
// tienen todos los cuestionarios anteriores a que el campo existiera. La UI vieja lo
// ignora por completo (verificado el 2026-08-11 sobre `origin/main`), así que un
// cuestionario en `null` se responde igual que siempre.
//
// `label` es la opción como se elige, `boton` es lo que va a leer quien responde: son
// dos frases distintas a propósito ("Resolver y cerrar la tarea" se elige, "Responder y
// cerrar la tarea" se aprieta). Las dos salen de i18n porque el flujo de respuesta ya
// las tiene traducidas y el tenant las puede cambiar.
export const QUESTIONNAIRE_ACTIONS = [
  {
    value: 'resolve',
    label: 'question_group_action_resolve',
    boton: 'questionnaire_submit_resolve',
    description: 'Al responderlo, la tarea queda cerrada.',
  },
  {
    value: 'reassign',
    label: 'question_group_action_reassign',
    boton: 'questionnaire_submit_reassign',
    description: 'La tarea pasa al primer responsable disponible del rol que elijas.',
  },
  {
    value: null,
    label: 'question_group_action_none',
    boton: 'reply_answer',
    description: 'Se guardan las respuestas y la tarea sigue como está.',
  },
]

// El default del alta, el mismo del editor viejo (`NewAddOreditQuestionGroup.js:92`): un
// cuestionario de cierre que no cierra nada no le sirve a nadie, y quedarse en `null` por
// omisión es lo que dejaba a los cuestionarios del builder sin gestionar la tarea.
export const QUESTIONNAIRE_ACTION_DEFAULT = 'resolve'

// Un valor que no está en el catálogo cae en "no hace nada", que es lo que el flujo de
// respuesta hace con cualquier acción que no sepa interpretar.
export const accionDeCuestionario = (valor) =>
  QUESTIONNAIRE_ACTIONS.find((a) => a.value === (valor ?? null)) ||
  QUESTIONNAIRE_ACTIONS.find((a) => a.value === null)
